import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    Search, Plus, MapPin, Briefcase, ChevronRight, Grid3x3, List,
    X, Loader2, MapPinned, ShieldCheck, Phone, Mail, Calendar, Camera, GraduationCap,
    ExternalLink, Tablet,
} from 'lucide-react';

const ACCENT = '#00875A'; // signal green (inspector accent)
const AVATAR_COLORS = ['#34D399', '#60A5FA', '#FBBF24', '#F87171', '#A78BFA', '#22D3EE', '#FB923C', '#F472B6'];

function avatarColor(name) {
    if (!name) return AVATAR_COLORS[0];
    return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
}

function timeAgo(iso) {
    if (!iso) return null;
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'hace unos segundos';
    if (m < 60) return `hace ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `hace ${h} h`;
    const d = Math.floor(h / 24);
    return `hace ${d} d`;
}

export default function AdminInspectors() {
    const [users, setUsers] = useState([]);
    const [view, setView] = useState('grid');
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = () => api.get('/admin/users?role=inspector').then((r) => {
        setUsers(r.data || []);
    }).finally(() => setLoading(false));

    useEffect(() => { load(); }, []);

    const filtered = users.filter((u) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.city || '').toLowerCase().includes(q);
    });

    return (
        <AdminLayout title="Auditores">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-6 p-5"
                style={{ background: 'rgba(0,135,90,0.06)', border: '1px solid rgba(0,135,90,0.25)' }}
                data-testid="admin-auditor-app-card">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: ACCENT }}>
                        <Tablet className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <div className="font-display font-bold text-sm mb-1">Auditor App (PWA de campo)</div>
                        <div className="text-xs text-gray-500 max-w-md">
                            Acceso exclusivo de administrador para instalar y configurar la app del auditor en las tabletas designadas para trabajo de campo. Solo tú puedes distribuir este enlace.
                        </div>
                    </div>
                </div>
                <a href="/app" target="_blank" rel="noopener noreferrer"
                    data-testid="open-auditor-app-btn"
                    className="text-white px-5 py-3 font-bold text-sm inline-flex items-center gap-2 hover:opacity-90 shrink-0"
                    style={{ background: ACCENT }}>
                    <ExternalLink className="w-4 h-4" /> Abrir Auditor App
                </a>
            </div>

            <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2 px-4 py-3 flex-1 max-w-xl" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar auditores..."
                        data-testid="inspectors-search"
                        className="flex-1 bg-transparent border-0 outline-none text-sm p-0"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                        <button onClick={() => setView('grid')} data-testid="view-grid"
                            className="p-2.5" style={view === 'grid' ? { background: ACCENT, color: 'white' } : {}}>
                            <Grid3x3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setView('list')} data-testid="view-list"
                            className="p-2.5" style={view === 'list' ? { background: ACCENT, color: 'white' } : {}}>
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                    <button onClick={() => setShowModal(true)} data-testid="new-inspector-btn"
                        className="text-white px-5 py-3 font-bold inline-flex items-center gap-2 hover:opacity-90"
                        style={{ background: ACCENT }}>
                        <Plus className="w-4 h-4" /> Nuevo Auditor
                    </button>
                </div>
            </div>

            <div className="text-sm text-gray-500 mb-5">
                <span className="font-bold" style={{ color: 'var(--foreground)' }}>{filtered.length}</span> auditores encontrados
            </div>

            {loading ? (
                <div className="p-12 text-center text-gray-500">Cargando...</div>
            ) : filtered.length === 0 ? (
                <div className="p-16 text-center text-gray-500 border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    Aún no hay auditores. Da de alta el primero.
                </div>
            ) : view === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((u) => <InspectorCard key={u.id} u={u} />)}
                </div>
            ) : (
                <InspectorList items={filtered} />
            )}

            {showModal && <NewInspectorModal onClose={() => setShowModal(false)} onCreated={load} />}
        </AdminLayout>
    );
}

function InspectorCard({ u }) {
    const initial = (u.name || 'I').charAt(0).toUpperCase();
    const color = avatarColor(u.name);
    const hasGeo = u.latitude && u.longitude;
    return (
        <Link to={`/admin/inspectors/${u.id}`}
            className="block p-6 transition-transform hover:-translate-y-0.5" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid={`inspector-card-${u.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                    {u.photo_url ? (
                        <img src={u.photo_url} alt={u.name}
                            className="w-14 h-14 object-cover shrink-0 ring-2"
                            style={{ ringColor: color, borderRadius: '4px' }}
                            data-testid={`inspector-photo-${u.id}`} />
                    ) : (
                        <div className="w-14 h-14 flex items-center justify-center font-display font-black text-xl shrink-0" style={{ background: `${color}20`, color }}>
                            {initial}
                        </div>
                    )}
                    <div className="min-w-0">
                        <div className="font-display text-lg font-bold truncate">{u.name}</div>
                        <div className="text-xs text-gray-500 truncate">{u.email}</div>
                        {u.rfc && (
                            <div className="text-[11px] text-gray-400 font-mono tracking-wide mt-0.5">RFC: {u.rfc}</div>
                        )}
                    </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 shrink-0" style={{ background: u.active ? 'rgba(16,185,129,0.15)' : 'rgba(156,163,175,0.15)', color: u.active ? '#10B981' : '#9CA3AF' }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: u.active ? '#10B981' : '#9CA3AF' }} />
                    {u.active ? 'Activo' : 'Inactivo'}
                </span>
            </div>
            {u.locked_at && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 mb-3"
                    style={{ background: 'rgba(220,38,38,0.15)', color: '#DC2626' }} data-testid={`inspector-locked-${u.id}`}>
                    Cuenta bloqueada
                </span>
            )}
            <div className="space-y-2 text-sm border-t pt-4 mb-3" style={{ borderColor: 'var(--border)' }}>
                {u.phone && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <Phone className="w-3.5 h-3.5" /> {u.phone}
                    </div>
                )}
                {u.education && (
                    <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                        <GraduationCap className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span className="line-clamp-2">{u.education}</span>
                    </div>
                )}
                {(u.city || u.state) && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="truncate">{[u.city, u.state, u.country].filter(Boolean).join(', ')}</span>
                    </div>
                )}
                {hasGeo ? (
                    <span
                        onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            window.open(`https://www.google.com/maps?q=${u.latitude},${u.longitude}`, '_blank');
                        }}
                        className="flex items-center gap-2 text-sm font-medium hover:underline cursor-pointer" style={{ color: ACCENT }}>
                        <MapPinned className="w-3.5 h-3.5" /> Ver en mapa
                        {u.location_updated_at && (
                            <span className="text-xs text-gray-400 ml-1">· {timeAgo(u.location_updated_at)}</span>
                        )}
                    </span>
                ) : (
                    <div className="flex items-center gap-2 text-xs text-gray-400 italic">
                        <ShieldCheck className="w-3.5 h-3.5" /> Sin geolocalización registrada
                    </div>
                )}
            </div>
        </Link>
    );
}

function InspectorList({ items }) {
    return (
        <div className="border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b" style={{ borderColor: 'var(--border)' }}>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">Auditor</th>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">Teléfono</th>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">Ubicación</th>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">GPS</th>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">Estado</th>
                    </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                    {items.map((u) => (
                        <tr key={u.id} className="hover:bg-black/[0.02] dark:hover:bg-white/5">
                            <td className="px-5 py-4">
                                <div className="flex items-center gap-3">
                                    {u.photo_url ? (
                                        <img src={u.photo_url} alt={u.name} className="w-10 h-10 object-cover shrink-0" />
                                    ) : (
                                        <div className="w-10 h-10 flex items-center justify-center text-white font-bold text-xs shrink-0"
                                            style={{ background: avatarColor(u.name) }}>
                                            {(u.name || 'I').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <Link to={`/admin/inspectors/${u.id}`} className="font-bold truncate hover:underline">{u.name}</Link>
                                        <div className="text-xs text-gray-500 truncate">{u.email} {u.rfc && <span className="ml-1 font-mono">· {u.rfc}</span>}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-5 py-4 text-gray-600 dark:text-gray-400">{u.phone || '—'}</td>
                            <td className="px-5 py-4 text-gray-600 dark:text-gray-400">{[u.city, u.state].filter(Boolean).join(', ') || '—'}</td>
                            <td className="px-5 py-4">
                                {u.latitude && u.longitude ? (
                                    <a href={`https://www.google.com/maps?q=${u.latitude},${u.longitude}`} target="_blank" rel="noreferrer"
                                        className="text-xs underline" style={{ color: ACCENT }}>
                                        Ver mapa
                                    </a>
                                ) : '—'}
                            </td>
                            <td className="px-5 py-4">
                                <span className="text-xs font-bold" style={{ color: u.active ? '#10B981' : '#9CA3AF' }}>
                                    ● {u.active ? 'Activo' : 'Inactivo'}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function NewInspectorModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        name: '', email: '', password: '', company: '', role: 'inspector',
        phone: '', contact_person: '', rfc: '', education: '', photo_url: '',
        address: '', city: '', state: '', country: 'México',
        latitude: '', longitude: '', notes: '',
    });
    const [geoLoading, setGeoLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [photoError, setPhotoError] = useState('');
    const set = (k, v) => setForm({ ...form, [k]: v });

    const RFC_RE = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{2,3}$/i;

    const onPhotoChange = (e) => {
        setPhotoError('');
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith('image/')) {
            setPhotoError('Sólo se aceptan archivos de imagen'); return;
        }
        if (f.size > 4 * 1024 * 1024) {
            setPhotoError('La foto no debe exceder 4 MB'); return;
        }
        const reader = new FileReader();
        reader.onload = () => set('photo_url', reader.result);
        reader.readAsDataURL(f);
    };

    const captureGeo = () => {
        if (!navigator.geolocation) {
            toast.error('Tu navegador no soporta geolocalización');
            return;
        }
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setForm((f) => ({
                    ...f,
                    latitude: pos.coords.latitude.toFixed(6),
                    longitude: pos.coords.longitude.toFixed(6),
                }));
                toast.success('Ubicación capturada con éxito');
                setGeoLoading(false);
            },
            (err) => {
                toast.error(`No se pudo obtener ubicación: ${err.message}`);
                setGeoLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const submit = async (e) => {
        e.preventDefault();
        const rfc = form.rfc.trim().toUpperCase();
        if (!rfc) { toast.error('El RFC es obligatorio'); return; }
        if (!RFC_RE.test(rfc)) {
            toast.error('RFC inválido. Formato: 4 letras + 6 dígitos + 3 caracteres (ej. PEMA850512H12)');
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form, rfc };
            // Normalize empty strings to null
            for (const k of ['phone', 'address', 'city', 'state', 'country', 'contact_person', 'notes', 'education', 'photo_url', 'company']) {
                if (payload[k] === '') payload[k] = null;
            }
            if (payload.latitude) payload.latitude = parseFloat(payload.latitude);
            else payload.latitude = null;
            if (payload.longitude) payload.longitude = parseFloat(payload.longitude);
            else payload.longitude = null;
            await api.post('/admin/users', payload);
            toast.success('Auditor creado con éxito');
            onCreated(); onClose();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al crear'));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl flex flex-col max-h-[calc(100vh-2rem)]" data-testid="new-inspector-modal"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between px-6 py-4 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: ACCENT }}>Alta de auditor</div>
                        <h2 className="font-display text-xl font-black">Nuevo Auditor</h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/10"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
                    {/* Fotografía */}
                    <Section title="Fotografía" accent={ACCENT}>
                        <div className="flex items-center gap-4">
                            <div className="w-24 h-24 flex items-center justify-center border-2 border-dashed shrink-0 overflow-hidden"
                                style={{ borderColor: 'var(--border)', background: 'rgba(0,0,0,0.02)' }}>
                                {form.photo_url ? (
                                    <img src={form.photo_url} alt="Preview" className="w-full h-full object-cover" />
                                ) : (
                                    <Camera className="w-7 h-7 text-gray-400" />
                                )}
                            </div>
                            <div className="flex-1">
                                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 text-sm font-bold border hover:bg-black/5 dark:hover:bg-white/10"
                                    style={{ borderColor: 'var(--border)' }}>
                                    <Camera className="w-4 h-4" />
                                    {form.photo_url ? 'Cambiar foto' : 'Subir / Tomar foto'}
                                    <input type="file" accept="image/*" capture="user"
                                        data-testid="inspector-photo-input"
                                        onChange={onPhotoChange} className="hidden" />
                                </label>
                                <div className="text-xs text-gray-500 mt-2">JPG/PNG hasta 4 MB. En móvil podrás tomar la foto directamente con la cámara.</div>
                                {photoError && <div className="text-xs text-red-500 mt-1">{photoError}</div>}
                            </div>
                        </div>
                    </Section>

                    <Section title="Datos generales" accent={ACCENT}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input label="Nombre completo *" v={form.name} on={(v) => set('name', v)} req placeholder="Nombre del auditor" />
                            <Input label="Correo electrónico *" type="email" v={form.email} on={(v) => set('email', v)} req placeholder="auditor@empresa.com" />
                            <Input label="RFC *" v={form.rfc} on={(v) => set('rfc', v.toUpperCase())} req placeholder="PEMA850512H12" maxLength={13} />
                            <Input label="Teléfono" v={form.phone} on={(v) => set('phone', v)} placeholder="(+52) ..." />
                            <Input label="Contraseña inicial *" type="text" v={form.password} on={(v) => set('password', v)} req placeholder="Auto generada" />
                        </div>
                    </Section>

                    <Section title="Formación académica" accent={ACCENT}>
                        <div>
                            <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">Últimos estudios / certificaciones</label>
                            <textarea rows={3} value={form.education}
                                onChange={(e) => set('education', e.target.value)}
                                data-testid="inspector-education"
                                placeholder="Ej. Ingeniería Industrial (UNAM, 2019); Certificación OSHA 30 (2022); Auditor Líder ISO 9001 (2024)"
                                className="w-full px-3 py-2 border outline-none resize-none"
                                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                        </div>
                    </Section>

                    <Section title="Ubicación y geolocalización" accent={ACCENT}>
                        <Input label="Dirección" v={form.address} on={(v) => set('address', v)} placeholder="Calle, número, colonia" />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Input label="Ciudad" v={form.city} on={(v) => set('city', v)} placeholder="Cancún" />
                            <Input label="Estado" v={form.state} on={(v) => set('state', v)} placeholder="Quintana Roo" />
                            <Input label="País" v={form.country} on={(v) => set('country', v)} placeholder="México" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                            <Input label="Latitud" v={form.latitude} on={(v) => set('latitude', v)} placeholder="21.1619" />
                            <Input label="Longitud" v={form.longitude} on={(v) => set('longitude', v)} placeholder="-86.8515" />
                            <button type="button" onClick={captureGeo} disabled={geoLoading} data-testid="capture-geo"
                                className="text-white px-4 py-3 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                style={{ background: ACCENT }}>
                                {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPinned className="w-4 h-4" />}
                                {geoLoading ? 'Obteniendo...' : 'Capturar GPS'}
                            </button>
                        </div>
                        {form.latitude && form.longitude && (
                            <div className="text-xs text-gray-500">
                                📍 Coordenadas: {form.latitude}, {form.longitude} •{' '}
                                <a href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`} target="_blank" rel="noreferrer" className="underline" style={{ color: ACCENT }}>
                                    Ver en Google Maps ↗
                                </a>
                            </div>
                        )}
                    </Section>

                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">Notas internas</label>
                        <textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)}
                            className="w-full px-3 py-2 border outline-none resize-none"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex gap-2 justify-end shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <button type="button" onClick={onClose} className="px-5 py-2 border hover:bg-black/5 dark:hover:bg-white/10" style={{ borderColor: 'var(--border)' }}>Cancelar</button>
                    <button type="submit" disabled={submitting} data-testid="submit-new-inspector"
                        className="text-white px-6 py-2 font-bold inline-flex items-center gap-2 disabled:opacity-50"
                        style={{ background: ACCENT }}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Dar de alta
                    </button>
                </div>
            </form>
        </div>
    );
}

function Section({ title, accent, children }) {
    return (
        <div className="space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>{title}</div>
            {children}
        </div>
    );
}

function Input({ label, v, on, req, type = 'text', placeholder, maxLength }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">{label}</label>
            <input
                type={type} value={v} onChange={(e) => on(e.target.value)} required={req}
                placeholder={placeholder} maxLength={maxLength}
                className="w-full px-3 py-2 border outline-none"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
            />
        </div>
    );
}
